import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useI18n } from "../i18n";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
    SECTORS,
    USERS_RANGES,
    ADMIN_RANGES,
    THIRDPARTY_RANGES,
    ENVIRONMENTS,
    PRIORITIES,
    RISKS,
    EXISTING_CAPABILITIES,
    FREQ_OPTIONS,
    GOVERNANCE_QS,
    AUTH_QS,
    PRIV_QS,
    SECRETS_QS,
} from "../data/wizard";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initialPayload = () => ({
    lang: "fr",
    context: {
        sector: null,
        users_range: null,
        admin_count_range: null,
        thirdparty_count_range: null,
        environments: [],
        priorities: [],
    },
    risks: [],
    existing_capabilities: [],
    answers: {},
});

// Reusable subcomponents ---------------------------------------------------
function SegmentedList({ options, value, onChange, lang, testid, columns = 2 }) {
    return (
        <div className={`grid gap-3 grid-cols-1 sm:grid-cols-${columns}`} data-testid={testid}>
            {options.map((opt) => {
                const selected = value === opt.id;
                return (
                    <button
                        type="button"
                        key={opt.id}
                        onClick={() => onChange(opt.id)}
                        className={`group flex items-start gap-3 rounded-sm border p-4 text-left transition-colors ${
                            selected
                                ? "border-ariovis-bordeaux bg-[#fef3f8]"
                                : "border-slate-200 bg-white hover:border-slate-400"
                        }`}
                        data-testid={`${testid}-opt-${opt.id}`}
                    >
                        <span
                            className={`mt-1 h-3 w-3 shrink-0 rounded-full border ${
                                selected ? "border-ariovis-bordeaux bg-ariovis-bordeaux" : "border-slate-300"
                            }`}
                            aria-hidden
                        />
                        <span className="text-sm font-medium text-slate-900">{opt[lang]}</span>
                    </button>
                );
            })}
        </div>
    );
}

function MultiList({ options, values, onToggle, lang, testid }) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={testid}>
            {options.map((opt) => {
                const selected = values.includes(opt.id);
                return (
                    <button
                        type="button"
                        key={opt.id}
                        onClick={() => onToggle(opt.id)}
                        className={`flex items-start gap-3 rounded-sm border p-4 text-left transition-colors ${
                            selected
                                ? "border-ariovis-bordeaux bg-[#fef3f8]"
                                : "border-slate-200 bg-white hover:border-slate-400"
                        }`}
                        data-testid={`${testid}-opt-${opt.id}`}
                    >
                        <span
                            className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                                selected ? "border-ariovis-bordeaux bg-ariovis-bordeaux text-white" : "border-slate-300 bg-white"
                            }`}
                            aria-hidden
                        >
                            {selected && <span className="text-[10px]">✓</span>}
                        </span>
                        <span className="text-sm font-medium text-slate-900">{opt[lang]}</span>
                    </button>
                );
            })}
        </div>
    );
}

function FrequencyPicker({ value, onChange, lang, testid }) {
    return (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid={testid}>
            {FREQ_OPTIONS.map((opt) => {
                const selected = value === opt.id;
                return (
                    <button
                        type="button"
                        key={opt.id}
                        onClick={() => onChange(opt.id)}
                        className={`min-h-[3rem] rounded-sm border px-2 py-2 text-center text-xs font-medium leading-tight [overflow-wrap:anywhere] hyphens-auto transition-colors ${
                            selected
                                ? "border-ariovis-bordeaux bg-ariovis-bordeaux text-white"
                                : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                        }`}
                        data-testid={`${testid}-${opt.id}`}
                    >
                        {opt[lang]}
                    </button>
                );
            })}
        </div>
    );
}

function QuestionBlock({ questions, values, onChange, lang, keyPrefix }) {
    return (
        <div className="space-y-6">
            {questions.map((q) => (
                <div key={q.id} className="rounded-sm border border-slate-200 bg-white p-5" data-testid={`${keyPrefix}-q-${q.id}`}>
                    <div className="mb-3 text-sm font-medium text-slate-900">{q[lang]}</div>
                    <FrequencyPicker
                        value={values[q.id]}
                        onChange={(v) => onChange(q.id, v)}
                        lang={lang}
                        testid={`${keyPrefix}-freq-${q.id}`}
                    />
                </div>
            ))}
        </div>
    );
}

// Main wizard --------------------------------------------------------------
export default function Wizard() {
    const { lang, t } = useI18n();
    const navigate = useNavigate();
    const { lang: urlLang } = useParams();
    const [step, setStep] = useState(0);
    const [payload, setPayload] = useState(() => initialPayload());
    const [lead, setLead] = useState({
        firstname: "",
        lastname: "",
        email: "",
        company: "",
        consent: true,
    });
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setPayload((p) => ({ ...p, lang }));
    }, [lang]);

    const STEPS = t("wizard.step_titles");
    const totalSteps = STEPS.length;

    const progressPct = ((step + 1) / totalSteps) * 100;

    const goNext = async () => {
        if (step === totalSteps - 2) {
            // About to reach lead gate: request preview
            try {
                const res = await axios.post(`${API}/preview`, payload);
                setPreview(res.data);
            } catch (e) {
                toast.error(t("wizard.error_generic"));
                return;
            }
        }
        setStep((s) => Math.min(totalSteps - 1, s + 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goPrev = () => {
        setStep((s) => Math.max(0, s - 1));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const submit = async () => {
        if (!lead.firstname || !lead.lastname || !lead.email || !lead.company) {
            toast.error(lang === "fr" ? "Merci de compléter les champs requis." : "Please fill required fields.");
            return;
        }
        if (!lead.consent) {
            toast.error(
                lang === "fr"
                    ? "Le consentement est requis pour recevoir le rapport."
                    : "Consent is required to receive the report."
            );
            return;
        }
        setSubmitting(true);
        try {
            const payloadLead = {
                firstname: lead.firstname,
                lastname: lead.lastname,
                email: lead.email,
                company: lead.company,
                phone: "",
                consent_report: true,
                consent_contact: true,
            };
            const res = await axios.post(`${API}/submit`, { payload, lead: payloadLead });
            const { token } = res.data;
            toast.success(
                lang === "fr" ? "Votre rapport a été envoyé par email." : "Your report has been sent by email."
            );
            navigate(`/${lang}/results?t=${encodeURIComponent(token)}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : t("wizard.error_generic"));
        } finally {
            setSubmitting(false);
        }
    };

    // Payload updaters -----------------------------------------------------
    const setCtx = (k, v) => setPayload((p) => ({ ...p, context: { ...p.context, [k]: v } }));
    const toggleCtxMulti = (k, id) =>
        setPayload((p) => {
            const cur = new Set(p.context[k] || []);
            cur.has(id) ? cur.delete(id) : cur.add(id);
            return { ...p, context: { ...p.context, [k]: Array.from(cur) } };
        });
    const toggleRisk = (id) =>
        setPayload((p) => {
            const cur = new Set(p.risks);
            cur.has(id) ? cur.delete(id) : cur.add(id);
            return { ...p, risks: Array.from(cur) };
        });
    const toggleCap = (id) =>
        setPayload((p) => {
            const cur = new Set(p.existing_capabilities);
            cur.has(id) ? cur.delete(id) : cur.add(id);
            return { ...p, existing_capabilities: Array.from(cur) };
        });
    const setAnswer = (qid, v) =>
        setPayload((p) => ({ ...p, answers: { ...p.answers, [qid]: v } }));

    const stepValid = useMemo(() => {
        if (step === 0) return !!payload.context.sector && !!payload.context.users_range;
        return true;
    }, [step, payload]);

    return (
        <>
            <Seo
                title={
                    lang === "fr"
                        ? "Diagnostic PAM — Ma Roadmap PAM | Ariovis"
                        : "PAM Diagnostic — Ma Roadmap PAM | Ariovis"
                }
                description={
                    lang === "fr"
                        ? "Répondez au diagnostic PAM en 7 étapes et recevez votre feuille de route personnalisée."
                        : "Answer the 7-step PAM diagnostic and receive your personalized roadmap."
                }
                path={`/${urlLang || lang}/wizard`}
                lang={lang}
                noindex
            />
            <div className="relative overflow-hidden bg-slate-50 py-14 sm:py-20">
                <div className="wizard-motif absolute inset-0" aria-hidden />
                <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <div className="mb-8">
                        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
                            <span>
                                {t("wizard.step_of")} {step + 1} {t("wizard.of")} {totalSteps}
                            </span>
                            <span data-testid="wizard-progress-label">{Math.round(progressPct)}%</span>
                        </div>
                        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                            <div
                                className="h-full brand-gradient transition-all duration-500 ease-out"
                                style={{ width: `${progressPct}%` }}
                                data-testid="wizard-progress-bar"
                            />
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.28 }}
                        >
                            <div className="rounded-md border border-slate-200 bg-white p-6 sm:p-8">
                                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-ariovis-bordeaux">
                                    {STEPS[step]}
                                </div>
                                <h2 className="mt-2 font-display text-2xl font-semibold text-slate-950 sm:text-3xl">
                                    {t("wizard.step_subtitles")[step]}
                                </h2>

                                <div className="mt-8 space-y-8">
                                    {/* Step 0 : Context */}
                                    {step === 0 && (
                                        <div className="space-y-8" data-testid="step-context">
                                            <div>
                                                <Label className="text-sm font-semibold text-slate-900">
                                                    {lang === "fr" ? "Secteur" : "Sector"}
                                                </Label>
                                                <div className="mt-3">
                                                    <SegmentedList
                                                        options={SECTORS}
                                                        value={payload.context.sector}
                                                        onChange={(v) => setCtx("sector", v)}
                                                        lang={lang}
                                                        testid="sector"
                                                        columns={3}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-sm font-semibold text-slate-900">
                                                    {lang === "fr" ? "Nombre d'utilisateurs" : "Number of users"}
                                                </Label>
                                                <div className="mt-3">
                                                    <SegmentedList
                                                        options={USERS_RANGES}
                                                        value={payload.context.users_range}
                                                        onChange={(v) => setCtx("users_range", v)}
                                                        lang={lang}
                                                        testid="users"
                                                        columns={5}
                                                    />
                                                </div>
                                            </div>
                                            <div className="grid gap-6 md:grid-cols-2">
                                                <div>
                                                    <Label className="text-sm font-semibold text-slate-900">
                                                        {lang === "fr" ? "Administrateurs" : "Administrators"}
                                                    </Label>
                                                    <div className="mt-3">
                                                        <SegmentedList
                                                            options={ADMIN_RANGES}
                                                            value={payload.context.admin_count_range}
                                                            onChange={(v) => setCtx("admin_count_range", v)}
                                                            lang={lang}
                                                            testid="admins"
                                                            columns={2}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-sm font-semibold text-slate-900">
                                                        {lang === "fr" ? "Prestataires techniques" : "Technical third-parties"}
                                                    </Label>
                                                    <div className="mt-3">
                                                        <SegmentedList
                                                            options={THIRDPARTY_RANGES}
                                                            value={payload.context.thirdparty_count_range}
                                                            onChange={(v) => setCtx("thirdparty_count_range", v)}
                                                            lang={lang}
                                                            testid="thirdparty"
                                                            columns={2}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-sm font-semibold text-slate-900">
                                                    {lang === "fr" ? "Environnement" : "Environment"}
                                                </Label>
                                                <div className="mt-3">
                                                    <MultiList
                                                        options={ENVIRONMENTS}
                                                        values={payload.context.environments}
                                                        onToggle={(id) => toggleCtxMulti("environments", id)}
                                                        lang={lang}
                                                        testid="environments"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="text-sm font-semibold text-slate-900">
                                                    {lang === "fr" ? "Enjeux principaux (plusieurs choix possibles)" : "Main priorities (multiple)"}
                                                </Label>
                                                <div className="mt-3">
                                                    <MultiList
                                                        options={PRIORITIES}
                                                        values={payload.context.priorities}
                                                        onToggle={(id) => toggleCtxMulti("priorities", id)}
                                                        lang={lang}
                                                        testid="priorities"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 1 : Risks */}
                                    {step === 1 && (
                                        <div data-testid="step-risks">
                                            <Label className="text-sm font-semibold text-slate-900">
                                                {lang === "fr" ? "Sélectionnez les situations qui vous concernent" : "Select the situations that apply"}
                                            </Label>
                                            <div className="mt-4">
                                                <MultiList
                                                    options={RISKS}
                                                    values={payload.risks}
                                                    onToggle={toggleRisk}
                                                    lang={lang}
                                                    testid="risks"
                                                />
                                            </div>
                                            <div className="mt-8">
                                                <Label className="text-sm font-semibold text-slate-900">
                                                    {lang === "fr"
                                                        ? "Capacités PAM déjà en place (le cas échéant)"
                                                        : "PAM capabilities already in place (if any)"}
                                                </Label>
                                                <div className="mt-4">
                                                    <MultiList
                                                        options={EXISTING_CAPABILITIES}
                                                        values={payload.existing_capabilities}
                                                        onToggle={toggleCap}
                                                        lang={lang}
                                                        testid="capabilities"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Step 2 : Governance */}
                                    {step === 2 && (
                                        <QuestionBlock questions={GOVERNANCE_QS} values={payload.answers} onChange={setAnswer} lang={lang} keyPrefix="gov" />
                                    )}
                                    {/* Step 3 : Auth */}
                                    {step === 3 && (
                                        <QuestionBlock questions={AUTH_QS} values={payload.answers} onChange={setAnswer} lang={lang} keyPrefix="auth" />
                                    )}
                                    {/* Step 4 : Priv */}
                                    {step === 4 && (
                                        <QuestionBlock questions={PRIV_QS} values={payload.answers} onChange={setAnswer} lang={lang} keyPrefix="priv" />
                                    )}
                                    {/* Step 5 : Secrets */}
                                    {step === 5 && (
                                        <QuestionBlock questions={SECRETS_QS} values={payload.answers} onChange={setAnswer} lang={lang} keyPrefix="sec" />
                                    )}

                                    {/* Step 6 : Lead gate */}
                                    {step === 6 && (
                                        <div className="space-y-6" data-testid="step-lead-gate">
                                            {/* Teaser */}
                                            <div className="rounded-md border border-slate-200 bg-slate-950 p-6 text-slate-100">
                                                <div className="text-xs font-semibold uppercase tracking-[0.24em] text-ariovis-pink">
                                                    {t("wizard.preview_label")}
                                                </div>
                                                {preview ? (
                                                    <>
                                                        <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                                            <div>
                                                                <div className="text-[11px] uppercase tracking-widest text-slate-400">
                                                                    {t("wizard.current_level_short")}
                                                                </div>
                                                                <div className="mt-1 text-2xl font-semibold text-white">
                                                                    N{preview.current_level} · {preview.current_level_name}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="text-[11px] uppercase tracking-widest text-slate-400">
                                                                    {t("wizard.target_level_short")}
                                                                </div>
                                                                <div className="mt-1 text-2xl font-semibold text-ariovis-pink">
                                                                    N{preview.target_level} · {preview.target_level_name}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="mt-4">
                                                            <div className="text-[11px] uppercase tracking-widest text-slate-400">
                                                                {t("wizard.axes_to_work")}
                                                            </div>
                                                            <ul className="mt-2 space-y-1 teaser-blur">
                                                                {(preview.axes || []).map((a) => (
                                                                    <li key={a.dim} className="text-sm text-slate-200">
                                                                        · {a.label}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="mt-4 text-sm text-slate-400">
                                                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                                                        {lang === "fr" ? "Calcul en cours…" : "Computing…"}
                                                    </div>
                                                )}
                                            </div>

                                            <h3 className="font-display text-xl font-semibold text-slate-950">
                                                {t("wizard.teaser_title")}
                                            </h3>
                                            <p className="text-sm text-slate-600">{t("wizard.teaser_desc")}</p>

                                            <div className="grid gap-4 sm:grid-cols-2">
                                                <div>
                                                    <Label htmlFor="firstname">{t("wizard.firstname")}</Label>
                                                    <Input
                                                        id="firstname"
                                                        value={lead.firstname}
                                                        onChange={(e) => setLead({ ...lead, firstname: e.target.value })}
                                                        data-testid="lead-firstname"
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="lastname">{t("wizard.lastname")}</Label>
                                                    <Input
                                                        id="lastname"
                                                        value={lead.lastname}
                                                        onChange={(e) => setLead({ ...lead, lastname: e.target.value })}
                                                        data-testid="lead-lastname"
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="email">{t("wizard.email")}</Label>
                                                    <Input
                                                        id="email"
                                                        type="email"
                                                        value={lead.email}
                                                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                                                        data-testid="lead-email"
                                                        className="mt-1"
                                                    />
                                                </div>
                                                <div>
                                                    <Label htmlFor="company">{t("wizard.company")}</Label>
                                                    <Input
                                                        id="company"
                                                        value={lead.company}
                                                        onChange={(e) => setLead({ ...lead, company: e.target.value })}
                                                        data-testid="lead-company"
                                                        className="mt-1"
                                                    />
                                                </div>
                                            </div>

                                            <label className="flex items-start gap-3 text-sm text-slate-700" data-testid="consent-wrapper">
                                                <Checkbox
                                                    checked={lead.consent}
                                                    onCheckedChange={(v) => setLead({ ...lead, consent: !!v })}
                                                    data-testid="lead-consent"
                                                />
                                                <span>{t("wizard.consent")}</span>
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {/* Navigation */}
                                <div className="mt-10 flex items-center justify-between">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={goPrev}
                                        disabled={step === 0}
                                        data-testid="wizard-prev-btn"
                                        className="text-slate-700"
                                    >
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        {t("wizard.back")}
                                    </Button>
                                    {step < totalSteps - 1 ? (
                                        <Button
                                            type="button"
                                            onClick={goNext}
                                            disabled={!stepValid}
                                            className="rounded-sm bg-ariovis-bordeaux text-white hover:bg-ariovis-fuschia"
                                            data-testid="wizard-next-btn"
                                        >
                                            {step === totalSteps - 2 ? t("wizard.finish") : t("wizard.next")}
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    ) : (
                                        <Button
                                            type="button"
                                            onClick={submit}
                                            disabled={submitting}
                                            className="rounded-sm bg-ariovis-bordeaux text-white hover:bg-ariovis-fuschia"
                                            data-testid="wizard-submit-btn"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t("wizard.sending")}
                                                </>
                                            ) : (
                                                t("wizard.submit")
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </>
    );
}
